/**
 * Обработчики вставки (cmd+v) и drag-and-drop изображений для Tiptap.
 *
 * При вставке/дропе файлов-изображений загружает их через `upload`
 * (возвращает URL вложения) и вставляет узел `image` в позицию курсора.
 * Узел `image` сериализуется tiptap-markdown в `![alt](url)`.
 *
 * Требует подключённого расширения `@tiptap/extension-image` (иначе
 * `schema.nodes.image` отсутствует — тогда тихо отдаём управление дефолту).
 */
import type { EditorView } from '@tiptap/pm/view';

export type ImageUploader = (file: File) => Promise<string>;

function isImage(file: File): boolean {
  return file.type.startsWith('image/');
}

function insertImages(view: EditorView, files: File[], upload: ImageUploader): boolean {
  const images = files.filter(isImage);
  if (images.length === 0) return false;
  const imageNode = view.state.schema.nodes.image;
  if (!imageNode) return false; // расширение Image не подключено

  void (async () => {
    for (const file of images) {
      try {
        const url = await upload(file);
        const node = imageNode.create({ src: url, alt: file.name });
        const tr = view.state.tr.replaceSelectionWith(node).scrollIntoView();
        view.dispatch(tr);
      } catch {
        // Ошибку показывает сам upload (message.error) — здесь молча
        // пропускаем файл, чтобы не падал весь paste.
      }
    }
  })();
  return true;
}

/**
 * Возвращает фрагмент `editorProps` для useEditor с handlePaste/handleDrop.
 * Подмешивать только когда есть `upload` (контекст пространства).
 */
export function makeImagePasteProps(upload: ImageUploader) {
  return {
    handlePaste: (view: EditorView, event: ClipboardEvent): boolean => {
      const files = event.clipboardData?.files;
      if (!files || files.length === 0) return false;
      return insertImages(view, Array.from(files), upload);
    },
    handleDrop: (view: EditorView, event: DragEvent): boolean => {
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return false;
      event.preventDefault();
      return insertImages(view, Array.from(files), upload);
    },
  };
}
